import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CatalogService from '@clickersphere/core/services/CatalogService';
import GalleryService from '@clickersphere/core/services/GalleryService';
import { applyImageWatermark } from '../../utils/applyImageWatermark';
import { detectImageMime } from '../../utils/detectImageMime';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { useUserMe } from '../user/useUserMe';
import { GALLERIES_QUERY_KEY } from './useGalleries';
import { GalleryDetailQueryData, buildGalleryDetailKey } from './useGallery';

export const MAX_GALLERY_PHOTO_BYTES = 15 * 1024 * 1024;
export const ACCEPTED_GALLERY_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
];

export type GalleryUploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export type GalleryUploadErrorKey =
  | 'tooLarge'
  | 'invalidType'
  | 'invalidContent'
  | 'processingFailed'
  | 'uploadFailed';

const WATERMARK_FALLBACK = 'Clicker Sphere';

export interface IGalleryUploadItem {
  id: string;
  name: string;
  progress: number;
  status: GalleryUploadStatus;
  errorKey?: GalleryUploadErrorKey;
}

export function useGalleryPhotoUpload(galleryId: string) {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { loggedInUser, selectedTenantId } = useUserMe();
  const [queue, setQueue] = useState<IGalleryUploadItem[]>([]);

  const watermarkText = useMemo(() => {
    const tenantName = loggedInUser?.memberships.find(
      (membership) => membership.tenant.id === selectedTenantId,
    )?.tenant.name;
    return `© ${tenantName?.trim() || WATERMARK_FALLBACK}`;
  }, [loggedInUser, selectedTenantId]);

  const updateItem = useCallback(
    (id: string, patch: Partial<IGalleryUploadItem>) => {
      setQueue((old) =>
        old.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const validate = useCallback(
    async (file: File): Promise<GalleryUploadErrorKey | null> => {
      if (file.size > MAX_GALLERY_PHOTO_BYTES) {
        return 'tooLarge';
      }
      if (!file.type || !ACCEPTED_GALLERY_PHOTO_TYPES.includes(file.type)) {
        return 'invalidType';
      }
      const detectedMime = await detectImageMime(file);
      if (!detectedMime || detectedMime !== file.type) {
        return 'invalidContent';
      }
      return null;
    },
    [],
  );

  const uploadOne = useCallback(
    async (id: string, file: File) => {
      const validationError = await validate(file);
      if (validationError) {
        updateItem(id, { status: 'error', errorKey: validationError });
        return;
      }

      updateItem(id, { status: 'uploading', progress: 0 });

      let processed: File;
      try {
        processed = await applyImageWatermark(file, watermarkText);
      } catch {
        updateItem(id, { status: 'error', errorKey: 'processingFailed' });
        return;
      }

      try {
        const { uploadUrl, fileKey } = await GalleryService.getPhotoUploadUrl(
          galleryId,
          { contentType: processed.type, fileSize: processed.size },
        );

        await CatalogService.uploadFileWithProgress(
          uploadUrl,
          processed,
          (percent) => updateItem(id, { progress: percent }),
        );

        const photo = await GalleryService.confirmPhoto(galleryId, { fileKey });

        queryClient.setQueryData<GalleryDetailQueryData>(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
          (old) =>
            old ? { ...old, photos: [...(old.photos ?? []), photo] } : old,
        );

        const detail = queryClient.getQueryData<GalleryDetailQueryData>(
          buildQueryKey(buildGalleryDetailKey(galleryId)),
        );
        if (detail?.leadId) {
          void queryClient.invalidateQueries({
            queryKey: buildQueryKey([GALLERIES_QUERY_KEY, detail.leadId]),
          });
        }

        updateItem(id, { status: 'success', progress: 100 });
      } catch {
        updateItem(id, { status: 'error', errorKey: 'uploadFailed' });
      }
    },
    [
      galleryId,
      validate,
      updateItem,
      queryClient,
      buildQueryKey,
      watermarkText,
    ],
  );

  const enqueue = useCallback(
    async (files: File[]) => {
      const items: { item: IGalleryUploadItem; file: File }[] = files.map(
        (file) => ({
          item: {
            id: crypto.randomUUID(),
            name: file.name,
            progress: 0,
            status: 'pending' as GalleryUploadStatus,
          },
          file,
        }),
      );

      setQueue((old) => [...old, ...items.map(({ item }) => item)]);

      for (const { item, file } of items) {
        await uploadOne(item.id, file);
      }
    },
    [uploadOne],
  );

  const clearFinished = useCallback(() => {
    setQueue((old) => old.filter((item) => item.status !== 'success'));
  }, []);

  const removeItem = useCallback((id: string) => {
    setQueue((old) => old.filter((item) => item.id !== id));
  }, []);

  const isUploading = queue.some(
    (item) => item.status === 'uploading' || item.status === 'pending',
  );

  return {
    queue,
    enqueue,
    clearFinished,
    removeItem,
    isUploading,
  };
}
